// src/components/admin/AdminQuestionErrors.tsx

import React, { useState, useEffect, useCallback } from 'react';
import {
    AlertTriangle,
    CheckCircle,
    XCircle,
    Trash2,
    ChevronDown,
    ChevronUp,
    BookOpen
} from 'lucide-react';
import './AdminQuestionErrors.css';

interface QuestionErrorReport {
    _id: string;
    questionId: {
        _id: string;
        statement: string;
        category: {
            level1?: string;
            level2?: string;
            level3?: string;
            level4?: string;
        };
        difficulty: string;
    } | null;
    userId: {
        username: string;
    } | null;
    description: string;
    status: 'pending' | 'accepted' | 'denied';
    resolvedAt: string | null;
    createdAt: string;
    questionSnapshot: {
        statement: string;
        category: {
            level1?: string;
            level2?: string;
            level3?: string;
            level4?: string;
        };
        difficulty: string;
    };
}

interface AdminQuestionErrorsProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

function stripHtml(html: string): string {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
}

const AdminQuestionErrors: React.FC<AdminQuestionErrorsProps> = ({ onShowToast }) => {
    const [reports, setReports] = useState<QuestionErrorReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const loadReports = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/admin/questions/error-reports/all', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error();
            const data = await response.json();
            setReports(data.reports);
        } catch {
            onShowToast('Erro ao carregar reportes', 'error');
        } finally {
            setLoading(false);
        }
    }, [onShowToast]);

    useEffect(() => {
        loadReports();
    }, [loadReports]);

    const handleUpdateStatus = async (reportId: string, status: 'accepted' | 'denied') => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`/api/admin/questions/error-reports/${reportId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ status })
            });
            if (!response.ok) throw new Error();
            onShowToast('Status atualizado', 'success');
            loadReports();
        } catch {
            onShowToast('Erro ao atualizar status', 'error');
        }
    };

    const handleDelete = async (reportId: string) => {
        if (!window.confirm('Deletar este reporte?')) return;
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`/api/admin/questions/error-reports/${reportId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error();
            onShowToast('Reporte deletado', 'success');
            loadReports();
        } catch {
            onShowToast('Erro ao deletar', 'error');
        }
    };

    const getStatusIcon = (status: string) => {
        if (status === 'pending') return <AlertTriangle size={15} />;
        if (status === 'accepted') return <CheckCircle size={15} />;
        return <XCircle size={15} />;
    };

    const getStatusLabel = (status: string) => {
        if (status === 'pending') return 'Pendente';
        if (status === 'accepted') return 'Aceito';
        return 'Negado';
    };

    const getCategoryPath = (cat?: { level1?: string; level2?: string; level3?: string; level4?: string }) => {
        if (!cat) return '';
        return [cat.level1, cat.level2, cat.level3, cat.level4]
            .filter(Boolean)
            .join(' › ');
    };

    const pendingCount = reports.filter(r => r.status === 'pending').length;

    if (loading) {
        return (
            <div className="aqe-loading">
                <div className="aqe-spinner" />
                <p>Carregando reportes...</p>
            </div>
        );
    }

    return (
        <div className="aqe-container">

            <div className="aqe-header">
                <div>
                    <p className="aqe-subtitle">
                        {reports.length} {reports.length === 1 ? 'reporte' : 'reportes'}
                        {pendingCount > 0 && ` · ${pendingCount} pendente${pendingCount !== 1 ? 's' : ''}`}
                    </p>
                </div>
            </div>

            {reports.length === 0 ? (
                <div className="aqe-empty">
                    <AlertTriangle size={48} />
                    <p>Nenhum erro reportado</p>
                </div>
            ) : (
                <div className="aqe-list">
                    {reports.map(report => {
                        const expanded = expandedId === report._id;
                        const snap = report.questionSnapshot;
                        const categoryPath = getCategoryPath(snap?.category);

                        return (
                            <div key={report._id} className={`aqe-card aqe-card--${report.status}`}>

                                <div className="aqe-card-header">
                                    <span className={`aqe-status-badge aqe-status-badge--${report.status}`}>
                                        {getStatusIcon(report.status)}
                                        {getStatusLabel(report.status)}
                                    </span>
                                    {categoryPath && (
                                        <span className="aqe-category">{categoryPath}</span>
                                    )}
                                    {snap?.difficulty && (
                                        <span className={`aqe-diff aqe-diff--${snap.difficulty}`}>
                                            {snap.difficulty === 'facil' ? 'Fácil' : snap.difficulty === 'medio' ? 'Médio' : 'Difícil'}
                                        </span>
                                    )}
                                </div>

                                <p className="aqe-description">
                                    <strong>Reporte:</strong> {report.description}
                                </p>

                                {snap?.statement && (
                                    <div className="aqe-question-preview">
                                        <button
                                            className="aqe-expand-btn"
                                            onClick={() => setExpandedId(expanded ? null : report._id)}
                                        >
                                            <BookOpen size={13} />
                                            <span>{expanded ? 'Ocultar questão' : 'Ver questão'}</span>
                                            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                        </button>
                                        {expanded && (
                                            <div className="aqe-question-body">
                                                {stripHtml(snap.statement)}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="aqe-meta">
                                    <span>
                                        Reportado por: <strong>{report.userId?.username ?? 'Usuário removido'}</strong>
                                    </span>
                                    <span className="aqe-meta-sep">·</span>
                                    <span>{new Date(report.createdAt).toLocaleDateString('pt-BR')}</span>
                                </div>

                                <div className="aqe-actions">
                                    {report.status === 'pending' && (
                                        <>
                                            <button
                                                className="aqe-btn aqe-btn--accept"
                                                onClick={() => handleUpdateStatus(report._id, 'accepted')}
                                            >
                                                <CheckCircle size={15} />
                                                Aceitar
                                            </button>
                                            <button
                                                className="aqe-btn aqe-btn--deny"
                                                onClick={() => handleUpdateStatus(report._id, 'denied')}
                                            >
                                                <XCircle size={15} />
                                                Negar
                                            </button>
                                        </>
                                    )}
                                    {report.status !== 'pending' && (
                                        <span className="aqe-resolved-label">
                                            Resolvido em {report.resolvedAt
                                                ? new Date(report.resolvedAt).toLocaleDateString('pt-BR')
                                                : new Date(report.createdAt).toLocaleDateString('pt-BR')
                                            }
                                        </span>
                                    )}
                                    <button
                                        className="aqe-btn aqe-btn--delete"
                                        onClick={() => handleDelete(report._id)}
                                        title="Deletar"
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>

                            </div>
                        );
                    })}
                </div>
            )}

        </div>
    );
};

export default AdminQuestionErrors;